import type { ImageModel } from 'ai';
import { createFibonacciDelays, sleep } from '../shared/backoff';
import { createScopedLogger } from '../shared/debug-log';
import { nanoid } from 'nanoid';
import { triggerImageTaskCallback } from './task-callback-registry';

const CREATE_RETRY_DELAY_MS = 500;
const POLL_RETRY_ATTEMPTS = 3;
const POLL_RETRY_DELAY_MS = 1000;
const EXECUTE_REQUEST_TIMEOUT_MS = 90_000;

function isRetryableNetworkStatus(status: number): boolean {
    return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

function extractErrorDetail(data: any): string {
    if (!data) return '';
    const parts: string[] = [];
    const code = data?.error?.code;
    if (typeof code === 'string' && code) parts.push(code);
    const msg = data?.error?.message || data?.message || data?.msg || data?.detail;
    if (typeof msg === 'string' && msg) parts.push(msg);
    if (!parts.length && typeof data?.error === 'string' && data.error) parts.push(data.error);
    return parts.join(': ');
}

function isAbortError(error: unknown): boolean {
    return (
        error instanceof Error &&
        (error.name === 'AbortError' || error.message.toLowerCase().includes('abort'))
    );
}

// ==================== 任务创建 ====================
/**
 * 任务创建请求配置
 */
export type CreateTaskConfig = {
    /** 请求 URL */
    url: string;
    /** 请求方法 */
    method: 'GET' | 'POST' | 'PUT';
    /** 请求头 */
    headers: Record<string, string>;
    /** 请求体（FormData 时按 multipart/form-data 发送，勿手动设置 Content-Type） */
    body?: Record<string, any> | string | FormData;
    /**
     * 响应解析类型（可选）
     * - auto: 根据 content-type 自动判断（默认）
     * - json: 强制按 JSON 解析
     * - text: 强制按文本返回
     * - arrayBuffer: 强制按二进制返回（Uint8Array）
     */
    responseType?: 'auto' | 'json' | 'text' | 'arrayBuffer';
};

/**
 * 任务创建响应解析结果
 */
export type CreateTaskResult = {
    /** 任务 ID（异步任务必填） */
    taskId?: string;
    /** 若为同步模型，创建响应直接返回的图片 URL 列表 */
    immediateImageUrls?: string[];
    /** 若为同步模型，创建响应直接返回的图片二进制（无需再通过 URL 下载） */
    immediateImages?: Uint8Array[];
    /** 同步创建阶段即发生的错误（可选） */
    error?: string;
    /** 原始响应数据 */
    raw?: any;
};

// ==================== 任务轮询 ====================
/**
 * 任务轮询请求配置
 */
export type PollTaskConfig = {
    /** 请求 URL */
    url: string | ((taskId: string) => string);
    /** 请求方法 */
    method: 'GET' | 'POST';
    /** 请求头 */
    headers: Record<string, string>;
    /** 请求体 */
    body?: Record<string, any> | string;
};

/**
 * 任务轮询超时配置
 */
export type PollingConfig = {
    /** 超时时间（毫秒）默认 10 分钟 */
    timeoutMs?: number;
    /** Backoff 配置 */
    backoff?: {
        initialDelayMs?: number;
        maxSteps?: number;
        scale?: number;
        jitter?: number;
        maxDelayMs?: number;
    };
};

/**
 * 任务轮询状态枚举
 */
export enum TaskStatus {
    /** 处理中 */
    PROCESSING = 'processing',
    /** 成功 */
    SUCCESS = 'success',
    /** 失败 */
    FAILED = 'failed',
}

/**
 * 任务轮询响应解析结果
 */
export type PollTaskResult = {
    /** 任务状态 */
    status: TaskStatus;
    /** 图像 URL 数组 */
    imageUrls?: string[];
    /** 错误信息 */
    error?: string;
    /** 原始响应数据 */
    raw?: any;
};

// ==================== 图像模型生成参数 ====================
/**
 * 图像模型生成参数
 */
export type ImageGenerateParams = {
    prompt: string;
    n?: number;
    size?: string;
    aspectRatio?: string;
    seed?: number;
    providerOptions?: Record<string, any>;
    abortSignal?: AbortSignal;
    /**
     * 当上游是异步任务时，创建阶段会拿到 taskId。
     * 该回调用于将 taskId 透出给调用方（例如 API 路由写入 DB / 前端轮询）。
     */
    onTaskCreated?: (taskId: string) => void | Promise<void>;
    generationId?: string;
    userId?: string;
};

// ==================== 提供商配置 ====================
/**
 * 提供商配置
 */
export type ProviderConfig = {
    /** Base URL 环境变量名 */
    baseUrlEnv: string;
    /** API Key 环境变量名 */
    apiKeyEnv: string;
    /** Debug 环境变量名 */
    debugEnv?: string;
    /** 提供商名称 */
    providerName: string;
};

/**
 * 提供商上下文
 */
export type ProviderContext = {
    baseUrl: string;
    apiKey: string;
};

/**
 * 提供商适配器接口
 */
export interface ImageProviderAdapter {
    /**
     * 任务创建请求配置
     * 支持异步的以便在构建请求前进行预处理
     */
    buildCreateTaskRequest(
        modelId: string,
        params: ImageGenerateParams,
        context: ProviderContext
    ): CreateTaskConfig | Promise<CreateTaskConfig>;

    /**
     * 任务创建响应解析结果, 提取 taskId 并返回
     */
    parseCreateTaskResponse(
        modelId: string,
        response: Response,
        data: any
    ): CreateTaskResult;

    /**
     * 任务轮询请求配置
     */
    buildPollTaskRequest(
        modelId: string,
        taskId: string,
        params: ImageGenerateParams,
        context: ProviderContext
    ): PollTaskConfig;

    /**
     * 任务轮询响应解析结果, 判断状态并提取图像 URL 或错误信息并返回
     */
    parsePollTaskResponse(
        modelId: string,
        response: Response,
        data: any
    ): PollTaskResult;

    /**
     * （可选）构建结果拉取请求。
     * 用于二步轮询场景：poll 返回 SUCCESS 但 imageUrls 为空时，
     * 基类会调用此方法获取实际结果。
     */
    buildFetchResultRequest?(
        modelId: string,
        taskId: string,
        context: ProviderContext
    ): CreateTaskConfig | null;

    /**
     * （可选）解析结果拉取响应。
     * 若未实现，基类会回退到 parsePollTaskResponse。
     */
    parseFetchResultResponse?(
        modelId: string,
        response: Response,
        data: any
    ): PollTaskResult;
}

// 扩展 ImageModel 类型以包含 Task 相关方法
export type ExtendedImageModel = ImageModel & {
    createTask?: (params: ImageGenerateParams) => Promise<{ taskId?: string; immediateImageUrls?: string[]; immediateImages?: Uint8Array[] }>;
    pollTask?: (taskId: string, params?: ImageGenerateParams) => Promise<PollTaskResult>;
};

// ==================== 图像模型提供商基类 ====================
/**
 * 图像模型提供商基类
 */
export class BaseImageProvider {
    protected config: ProviderConfig;
    protected pollingConfig: PollingConfig;
    protected debug: boolean;
    protected nextDelay: () => number;

    constructor(
        config: ProviderConfig,
        pollingConfig: PollingConfig = {}
    ) {
        this.config = config;
        this.pollingConfig = {
            timeoutMs: 10 * 60 * 1000,
            backoff: {
                initialDelayMs: 5000,
                maxSteps: 20,
                scale: 1,
                jitter: 0.2,
                maxDelayMs: 10000,
            },
            ...pollingConfig,
        };
        this.debug = process.env[config.debugEnv || `${config.providerName.toUpperCase()}_DEBUG`] === 'true';
        this.nextDelay = createFibonacciDelays(this.pollingConfig.backoff);
    }

    /**
     * 获取 Base URL
     */
    protected getBaseUrl(): string {
        const base = process.env[this.config.baseUrlEnv];
        if (!base) {
            throw new Error(`Missing ${this.config.baseUrlEnv}`);
        }
        return base.replace(/\/$/, '');
    }

    /**
     * 获取 API Key
     */
    protected getApiKey(): string {
        const key = process.env[this.config.apiKeyEnv];
        if (!key) {
            throw new Error(`Missing ${this.config.apiKeyEnv}`);
        }
        return key;
    }

    /**
     * 创建日志记录器
     */
    protected createLogger(modelId: string, requestId: string, userId?: string) {
        return createScopedLogger({
            tags: [userId, this.config.providerName.toUpperCase(), modelId, requestId],
            debug: this.debug,
        });
    }

    /**
     * 执行 HTTP 请求
     */
    protected async executeRequest(config: CreateTaskConfig | PollTaskConfig, abortSignal?: AbortSignal): Promise<{ response: Response; data: any }> {
        const url = typeof config.url === 'function' ? config.url('') : config.url;
        const createConfig = config as CreateTaskConfig;
        const method = config.method;
        const headers = config.headers;

        let body: string | FormData | undefined;
        if (createConfig.body !== undefined) {
            if (typeof createConfig.body === 'string' || createConfig.body instanceof FormData) {
                body = createConfig.body;
            } else {
                body = JSON.stringify(createConfig.body);
            }
        }

        const timeoutSignal = AbortSignal.timeout(EXECUTE_REQUEST_TIMEOUT_MS);
        const signal = abortSignal
            ? AbortSignal.any([abortSignal, timeoutSignal])
            : timeoutSignal;

        const response = await fetch(url, {
            method: method,
            headers: headers,
            body: body,
            signal,
        });

        // 解析响应
        // - 兼容：部分提供商 content-type 不准确（text/plain 但实际是 JSON）
        // - 同步直出图片：支持 image/* 与 application/octet-stream
        let data: any = undefined;
        const responseType = (createConfig as any)?.responseType as CreateTaskConfig['responseType'] | undefined;
        const contentType = (response.headers.get('content-type') || '').toLowerCase();
        const looksLikeBinaryImage = contentType.startsWith('image/') || contentType.includes('application/octet-stream');

        if (responseType === 'arrayBuffer' || (responseType !== 'json' && responseType !== 'text' && looksLikeBinaryImage)) {
            const buffer = new Uint8Array(await response.arrayBuffer());
            data = buffer;
        } else {
            const text = await response.text();
            if (!text) {
                data = {};
            } else if (responseType === 'text') {
                data = text;
            } else {
                // auto/json：尽量按 JSON 解析；失败则保留原始文本（不要吞成 {}）
                try {
                    data = JSON.parse(text);
                } catch {
                    data = text;
                }
            }
        }

        return { response: response, data: data };
    }

    /**
     * 创建图像模型实例
     */
    createImageModel(
        modelId: string,
        adapter: ImageProviderAdapter,
        modelTimeoutMs?: number
    ): ExtendedImageModel {
        const createTask = async (params: ImageGenerateParams) => {
            const requestId = params.generationId || nanoid(10);
            const log = this.createLogger(modelId, requestId, params.userId);

            try {
                // 0. 准备提供商上下文
                const context: ProviderContext = {
                    baseUrl: this.getBaseUrl(),
                    apiKey: this.getApiKey(),
                };

                // 1. 创建任务
                log.debug('[create:start]', { params: params });
                const createConfigPromise = adapter.buildCreateTaskRequest(modelId, params, context);
                const createConfig = await Promise.resolve(createConfigPromise);
                // 传给模型的参数
                log.debug('[create:payload]', {
                    body: createConfig.body,
                });

                let createResp: Response | undefined;
                let createData: any = undefined;
                for (let attempt = 0; attempt < 2; attempt++) {
                    try {
                        const result = await this.executeRequest(createConfig, params.abortSignal);
                        createResp = result.response;
                        createData = result.data;

                        if (!createResp.ok) {
                            log.error('[create:http_error]', {
                                attempt: attempt + 1,
                                status: createResp.status,
                                statusText: createResp.statusText,
                                data: createData,
                            });

                            if (attempt === 0 && isRetryableNetworkStatus(createResp.status)) {
                                log.error('[create:retry_once]', { reason: 'retryable_status', status: createResp.status });
                                await sleep(CREATE_RETRY_DELAY_MS);
                                continue;
                            }

                            const detail = extractErrorDetail(createData);
                            throw new Error(`${modelId} task create failed: ${createResp.status} ${createResp.statusText}${detail ? ` — ${detail}` : ''}`);
                        }

                        break;
                    } catch (error) {
                        if (attempt === 0 && !isAbortError(error)) {
                            log.error('[create:request_error]', {
                                attempt: attempt + 1,
                                error: error instanceof Error ? error.message : String(error),
                            });
                            log.error('[create:retry_once]', { reason: 'fetch_error' });
                            await sleep(CREATE_RETRY_DELAY_MS);
                            continue;
                        }
                        throw error;
                    }
                }

                if (!createResp) {
                    throw new Error(`${modelId} task create failed: missing response`);
                }

                const createResult = adapter.parseCreateTaskResponse(modelId, createResp, createData);
                if (createResult.error) {
                    log.error('[create:failed]', { error: createResult.error, data: createResult.raw });
                    throw new Error(createResult.error);
                }

                // 同步模型：创建即返回最终结果，跳过轮询
                if (createResult.immediateImages && createResult.immediateImages.length > 0) {
                    return { immediateImages: createResult.immediateImages };
                }
                if (createResult.immediateImageUrls && createResult.immediateImageUrls.length > 0) {
                    return { immediateImageUrls: createResult.immediateImageUrls };
                }

                const taskId = createResult.taskId;

                if (!taskId) {
                    log.error('[create:missing_task_id]', { data: createData });
                    throw new Error('Failed to get task id');
                }

                log.debug('[create:success]', { taskId: taskId, data: createResult.raw });

                // 通过 providerOptions 传递 callback key（用于 AI SDK generateImage 场景）
                try {
                    const cbKey = (params as any)?.providerOptions?._flowmuse?.taskCbKey as string | undefined;
                    if (cbKey) {
                        await triggerImageTaskCallback(cbKey, taskId);
                    }
                } catch (e) {
                    log.error('[taskCbKey:trigger_failed]', { error: (e as any)?.message || String(e) });
                }

                // 将 taskId 透出给调用方（只触发一次，不影响轮询流程）
                try {
                    await Promise.resolve(params.onTaskCreated?.(taskId));
                } catch (e) {
                    log.error('[onTaskCreated:failed]', { error: (e as any)?.message || String(e) });
                }

                return { taskId };
            } catch (error) {
                log.error('[create:error]', {
                    error: error instanceof Error ? error.message : String(error),
                });
                throw error;
            }
        };

        const pollTask = async (taskId: string, params?: ImageGenerateParams) => {
            const safeParams = params || { prompt: '' } as ImageGenerateParams;
            const requestId = safeParams.generationId || taskId || nanoid(10);
            const log = this.createLogger(modelId, requestId, safeParams.userId);

            const context: ProviderContext = {
                baseUrl: this.getBaseUrl(),
                apiKey: this.getApiKey(),
            };

            // 轮询请求
            const pollConfig = adapter.buildPollTaskRequest(modelId, taskId, safeParams, context);
            const pollUrl = typeof pollConfig.url === 'function' ? pollConfig.url(taskId) : pollConfig.url;

            log.debug('[poll:request]', { taskId: taskId, url: pollUrl });

            // poll 请求层：对 408/429/5xx（以及 fetch 网络异常，非 abort）重试 3 次，每次 1000ms
            let pollResp: Response | undefined;
            let pollData: any = undefined;
            for (let attempt = 0; attempt < POLL_RETRY_ATTEMPTS; attempt++) {
                try {
                    const result = await this.executeRequest({
                        ...pollConfig,
                        url: pollUrl,
                    }, safeParams.abortSignal);
                    pollResp = result.response;
                    pollData = result.data;

                    if (!pollResp.ok) {
                        log.error('[poll:http_error]', {
                            attempt: attempt + 1,
                            status: pollResp.status,
                            statusText: pollResp.statusText,
                            data: pollData,
                        });

                        if (attempt < POLL_RETRY_ATTEMPTS - 1 && isRetryableNetworkStatus(pollResp.status)) {
                            log.error('[poll:retry]', { reason: 'retryable_status', status: pollResp.status, attempt: attempt + 1 });
                            await sleep(POLL_RETRY_DELAY_MS);
                            continue;
                        }

                        const detail = extractErrorDetail(pollData);
                        throw new Error(`${modelId} task poll failed: ${pollResp.status} ${pollResp.statusText}${detail ? ` — ${detail}` : ''}`);
                    }

                    break;
                } catch (error) {
                    if (isAbortError(error)) throw error;
                    log.error('[poll:request_error]', {
                        attempt: attempt + 1,
                        error: error instanceof Error ? error.message : String(error),
                    });
                    if (attempt < POLL_RETRY_ATTEMPTS - 1) {
                        log.error('[poll:retry]', { reason: 'fetch_error', attempt: attempt + 1 });
                        await sleep(POLL_RETRY_DELAY_MS);
                        continue;
                    }
                    throw error;
                }
            }

            if (!pollResp) {
                throw new Error(`${modelId} task poll failed: missing response`);
            }

            let pollResult = adapter.parsePollTaskResponse(modelId, pollResp, pollData);

            if (
                pollResult.status === TaskStatus.SUCCESS &&
                (!pollResult.imageUrls || pollResult.imageUrls.length === 0) &&
                adapter.buildFetchResultRequest
            ) {
                const resultConfig = adapter.buildFetchResultRequest(modelId, taskId, context);
                if (resultConfig) {
                    log.debug('[poll:fetch_result]', { taskId, url: resultConfig.url });
                    const resultUrl = typeof resultConfig.url === 'function'
                        ? (resultConfig.url as (id: string) => string)(taskId)
                        : resultConfig.url;

                    const { response: resultResp, data: resultData } = await this.executeRequest(
                        { ...resultConfig, url: resultUrl },
                        safeParams.abortSignal,
                    );

                    if (!resultResp.ok) {
                        log.error('[poll:fetch_result_failed]', {
                            status: resultResp.status,
                            statusText: resultResp.statusText,
                            data: resultData,
                        });

                        // 不可重试的错误（4xx 非 408/429）→ 返回 FAILED，不抛异常，避免 Inngest 重试
                        if (!isRetryableNetworkStatus(resultResp.status)) {
                            const adapterResult = adapter.parseFetchResultResponse?.(modelId, resultResp, resultData);
                            const adapterError = adapterResult?.status === TaskStatus.FAILED ? adapterResult.error : undefined;
                            const detail = adapterError || extractErrorDetail(resultData);
                            pollResult = {
                                status: TaskStatus.FAILED,
                                error: `${modelId} result fetch failed: ${resultResp.status}${detail ? ` — ${detail}` : ''}`,
                                raw: resultData,
                            };
                        } else {
                            // 可重试的错误（408/429/5xx）→ 抛异常，允许重试
                            const detail = extractErrorDetail(resultData);
                            throw new Error(
                                `${modelId} result fetch failed: ${resultResp.status} ${resultResp.statusText}${detail ? ` — ${detail}` : ''}`,
                            );
                        }
                    } else {
                        pollResult = adapter.parseFetchResultResponse
                            ? adapter.parseFetchResultResponse(modelId, resultResp, resultData)
                            : adapter.parsePollTaskResponse(modelId, resultResp, resultData);
                    }
                }
            }

            return pollResult;
        };

        return {
            specificationVersion: 'v1',
            provider: this.config.providerName.toLowerCase(),
            modelId: modelId,
            maxImagesPerCall: 1,
            createTask,
            pollTask,
            doGenerate: async (params: ImageGenerateParams) => {
                const requestId = params.generationId || nanoid(10);
                const log = this.createLogger(modelId, requestId, params.userId);

                const createResult = await createTask(params);

                // 同步返回
                if (createResult.immediateImages && createResult.immediateImages.length > 0) {
                    return {
                        image: createResult.immediateImages[0],
                        images: createResult.immediateImages,
                        warnings: [],
                        response: {
                            timestamp: new Date(),
                            modelId: modelId,
                            headers: undefined,
                        },
                    };
                }
                if (createResult.immediateImageUrls && createResult.immediateImageUrls.length > 0) {
                    log.debug('[create:sync_success]', { imageUrls: createResult.immediateImageUrls });
                    const buffers: Uint8Array[] = [];
                    for (const imageUrl of createResult.immediateImageUrls) {
                        const imageResp = await fetch(imageUrl, { signal: params.abortSignal });
                        if (!imageResp.ok) {
                            log.error('[create:sync_fetch_image_failed]', {
                                status: imageResp.status,
                                statusText: imageResp.statusText,
                                url: imageUrl,
                            });
                            throw new Error(`${modelId} task create failed: failed to fetch image`);
                        }
                        const buffer = new Uint8Array(await imageResp.arrayBuffer());
                        if (buffer.length === 0) {
                            log.error('[create:sync_image_buffer_empty]', { url: imageUrl });
                            throw new Error(`${modelId} task create failed: failed to convert image to buffer`);
                        }
                        buffers.push(buffer);
                    }
                    return {
                        image: buffers[0],
                        images: buffers,
                        warnings: [],
                        response: {
                            timestamp: new Date(),
                            modelId: modelId,
                            headers: undefined,
                        },
                    };
                }

                // 异步轮询
                const taskId = createResult.taskId!;
                const startTime = Date.now();
                const timeoutMs = modelTimeoutMs ?? this.pollingConfig.timeoutMs!;
                let iteration = 0;

                while (Date.now() - startTime < timeoutMs) {
                    await sleep(this.nextDelay());
                    iteration++;

                    try {
                        const pollResult = await pollTask(taskId, params);
                        const elapsedMs = Date.now() - startTime;

                        switch (pollResult.status) {
                            case TaskStatus.SUCCESS:
                                if (!pollResult.imageUrls || pollResult.imageUrls.length === 0) {
                                    log.error('[poll:success_but_missing_urls]', { data: pollResult.raw });
                                    throw new Error(`${modelId} task generate failed: missing image URLs`);
                                }
                                log.debug('[poll:success]', {
                                    iteration,
                                    elapsedMs,
                                    status: pollResult.status,
                                    data: pollResult.raw,
                                    imageUrls: pollResult.imageUrls,
                                });

                                // 下载图像
                                const buffers: Uint8Array[] = [];
                                for (const imageUrl of pollResult.imageUrls) {
                                    const imageResp = await fetch(imageUrl, { signal: params.abortSignal });
                                    if (!imageResp.ok) {
                                        log.error('[poll:failed_to_fetch_image]', {
                                            iteration,
                                            status: imageResp.status,
                                            statusText: imageResp.statusText,
                                            url: imageUrl,
                                        });
                                        throw new Error(`${modelId} task poll failed: failed to fetch image`);
                                    }
                                    const buffer = new Uint8Array(await imageResp.arrayBuffer());
                                    if (buffer.length === 0) {
                                        log.error('[poll:failed_to_convert_image_to_buffer]', {
                                            iteration,
                                            url: imageUrl,
                                        });
                                        throw new Error(`${modelId} task poll failed: failed to convert image to buffer`);
                                    }
                                    buffers.push(buffer);
                                }

                                return {
                                    image: buffers[0],
                                    images: buffers,
                                    warnings: [],
                                    response: {
                                        timestamp: new Date(),
                                        modelId: modelId,
                                        headers: undefined,
                                    },
                                };

                            case TaskStatus.FAILED:
                                log.error('[poll:failed]', {
                                    iteration,
                                    elapsedMs,
                                    status: pollResult.status,
                                    data: JSON.stringify(pollResult.raw, null, 2),
                                    error: pollResult.error,
                                });
                                throw new Error(`${pollResult.error || 'unknown error'}`);

                            case TaskStatus.PROCESSING:
                                log.debug('[poll:processing]', {
                                    iteration,
                                    elapsedMs,
                                    status: pollResult.status,
                                    data: pollResult.raw,
                                });
                                break;

                            default:
                                log.debug('[poll:unknown_status]', {
                                    iteration,
                                    elapsedMs,
                                    status: pollResult.status,
                                });
                                break;
                        }
                    } catch (error) {
                        // 轮询中的非致命错误（网络波动等）在 executeRequest 层已处理重试
                        // 这里捕获的是致命错误或超时抛出的错误
                        log.error('[generate:error]', {
                            error: error instanceof Error ? error.message : String(error),
                        });
                        throw error;
                    }
                }

                const elapsedMs = Date.now() - startTime;
                log.error('[poll:timeout]', {
                    iteration,
                    elapsedMs,
                });
                throw new Error(`Timeout`);
            },
        };
    }
}
